// Function to generate section data
const getSectionData = (id, section, selector) => ({ id, section, selector });

// Helper to reliably re-initialise the Rebuy widget after DOM updates.
// Rebuy.init() is asynchronous – if the widget container is still empty after
// the first call we retry once after a short delay so the widget has time to
// fetch & render its products.
function reinitRebuy(retries = 2, delay = 400) {
  if (!window.Rebuy) return;
  window.Rebuy.init();
  if (retries > 0) {
    setTimeout(() => {
      const widgetEl = document.querySelector('[data-rebuy-id]');
      // If the widget has rendered product content it will contain more than
      // just an empty div, so we check for at least one child element.
      const isEmpty = !widgetEl || widgetEl.children.length === 0;
      if (isEmpty && window.Rebuy) {
        window.Rebuy.init();
        retries > 1 && setTimeout(() => window.Rebuy && window.Rebuy.init(), delay * 2);
      }
    }, delay);
  }
}

// Check cart drawer and set sections to render
const cartDrawer = document.querySelector('cart-drawer');
let sectionsToRender = [];

if (cartDrawer) {
  const mainCartId = document.getElementById('main-cart-items')?.dataset.id;
  if (mainCartId) {
    sectionsToRender = [
      getSectionData(`#shopify-section-${mainCartId}`, mainCartId, `#shopify-section-${mainCartId} cart-items`),
      getSectionData("#cart-counter", "cart-counter", "#shopify-section-cart-counter"),
      getSectionData("#CartDrawer-Body", "cart-drawer", "#shopify-section-cart-drawer #CartDrawer-Body")
    ];
  } else {
    sectionsToRender = [
      getSectionData("#CartDrawer-Body", "cart-drawer", "#shopify-section-cart-drawer #CartDrawer-Body")
    ];
  }
} else {
  const mainCartId = document.getElementById('main-cart-items')?.dataset.id;
  if (mainCartId) {
    sectionsToRender = [
      getSectionData(`#shopify-section-${mainCartId}`, mainCartId, `#shopify-section-${mainCartId} cart-items`),
      getSectionData('#cart-counter', 'cart-counter', '#shopify-section-cart-counter')
    ];
  } else {
    sectionsToRender = [];
  }
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', event => {
      event.preventDefault();
      const cartItems =
        this.closest('cart-drawer-items') ||
        this.closest('cart-items');
      cartItems.updateQuantity(this.dataset.index, 0);
      updateFreeShipping();
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.freeShipping = document.querySelectorAll('shipping-bar');

    this.currentItemCount = Array.from(
      this.querySelectorAll('[name="updates[]"]')
    ).reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    this.debouncedOnChange = debounce(event => {
      this.onChange(event);
    }, 300);
    this.addEventListener(
      'change', this.debouncedOnChange.bind(this)
    );

    updateFreeShipping();
  }

  calculateTotalItemCount(items) {
    return items.reduce((total, item) => total + item.quantity, 0);
  }

  onChange(event) {
    if (event.target.name !== 'updates[]') return;

    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name')
    );
  }

  getSectionsToRender() {
    return sectionsToRender;
  }

  updateQuantity(line, quantity, name) {
    this.classList.add('is-loading');

    // Save Rebuy widget rendered HTML before DOM gets replaced.
    // After replacement the container is an empty div again, so restoring
    // the saved markup prevents the "title only" flash while Rebuy refreshes.
    const rebuyEl = document.querySelector('[data-rebuy-id]');
    const savedRebuyHTML = rebuyEl ? rebuyEl.innerHTML : null;

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map(section => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      ...{ body }
    })
      .then(response => response.text())
      .then(state => {
        const parsedState = JSON.parse(state);
        this.getSectionsToRender()?.forEach(section => {
          const elementToReplace = document.querySelector(section.selector) || document.querySelector(section.id);

          if (elementToReplace) {
            if (!parsedState.errors) {
              elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector
              );
            }
          } else {
            console.error(`Element with selector ${section.selector} not found`);
          }
        });

        // Restore saved Rebuy content so the widget stays visible immediately.
        if (savedRebuyHTML) {
          const newRebuyEl = document.querySelector('[data-rebuy-id]');
          if (newRebuyEl && newRebuyEl.innerHTML.trim() === '') {
            newRebuyEl.innerHTML = savedRebuyHTML;
          }
        }

        // Give Rebuy time after DOM is stable, then re-init so it can
        // refresh its recommendations based on the updated cart.
        setTimeout(() => reinitRebuy(), 150);

        if (!parsedState.errors) {
          this.totalItemCount = this.calculateTotalItemCount(parsedState.items);
        }
        this.updateLiveRegions(line, parsedState.item_count, parsedState.errors);

        const lineItem = document.getElementById(`CartItem-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`))
          lineItem.querySelector(`[name="${name}"]`).focus();

        updateCartCounters();
        updateFreeShipping();
      })
      .finally(() => this.classList.remove('is-loading'));
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  updateLiveRegions(line, itemCount, parsedError) {
    if (parsedError) {
      document
        .querySelectorAll(`[data-line-item-error][data-line="${line}"]`)
        .forEach(error => {
          error.innerHTML = parsedError;
        });
    }

    this.currentItemCount = itemCount;
  }
}
customElements.define('cart-items', CartItems);

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("keyup", event => event.code.toUpperCase() === "ESCAPE" && this.close());
    this.setCartLink();
    this.parentElement.addEventListener("shopify:section:select", () => this.open());
    this.parentElement.addEventListener("shopify:section:deselect", () => this.close());
  }

  setCartLink() {
    document.addEventListener("click", event => {
      const cartLink = event.target.closest("[data-cart-link]");
      if (cartLink) {
        event.preventDefault();
        this.open(cartLink);
      }
    });

    document.addEventListener("keydown", event => {
      const cartLink = event.target.closest("[data-cart-link]");
      if (cartLink && event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });

    document.querySelectorAll("[data-cart-link]").forEach(cartLink => {
      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");
    });
  }

  open(opener) {
    if (opener) this.setActiveElement(opener);
    this.classList.add("is-visible");
    document.querySelector('body').style.overflow = 'hidden';
    this.addEventListener("transitionend", () => { this.focusOnCartDrawer(); }, { once: true });

    setTimeout(() => {
      document.addEventListener("click", this.handleOutsideClick);
    }, 100);

    const productReccomendations = document.querySelector(".product-recommendations");
    if (productReccomendations) {
      if (productReccomendations.classList.contains("hidden")) {
        document.querySelector(".cart-drawer-items").classList.add("cart-drawer-items__full");
      } else {
        document.querySelector(".cart-drawer-items").classList.remove("cart-drawer-items__full");
      }
    }

    reinitRebuy();
  }

  close() {
    this.classList.remove("is-visible");
    document.querySelector('body').style.overflow = 'auto';
    removeTrapFocus(this.activeElement);

    document.removeEventListener("click", this.handleOutsideClick);

    const isHeaderMenuOpen = header.classList.contains("menu-open");

    if (isHeaderMenuOpen) {
      return;
    }

    // if we are on the cart page, resubmit form
    if (window.location.pathname === "/cart") {
      const cartDrawerForm = document.getElementById("CartDrawer-FormSummary");
      if (cartDrawerForm) {
        cartDrawerForm.submit();
      }
    }
  }

  handleOutsideClick = event => {
    const cartDrawerInner = this.querySelector(".cart-drawer__inner");
    if (cartDrawerInner && !cartDrawerInner.contains(event.target)) {
      this.close();
    }
  };

  setActiveElement(element) {
    this.activeElement = element;
  }

  focusOnCartDrawer() {
    const containerToTrapFocusOn = this.firstElementChild;
    const focusElement = this.querySelector("[data-drawer-close]");
    trapFocus(containerToTrapFocusOn, focusElement);
  }

  renderContents(response, open = true) {
    this.getSectionsToRender()?.forEach(section => {
      const sectionElement = document.querySelector(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        response.sections[section.section],
        section.selector
      );

      updateCartCounters();
    });

    reinitRebuy();

    if (!open) {
      return;
    }

    this.open();
  }

  getSectionsToRender() {
    return sectionsToRender;
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}
customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return sectionsToRender;
  }
}
customElements.define("cart-drawer-items", CartDrawerItems);


document.addEventListener('rebuy:cart.add', function(event){

    fetch('/cart.js')
    .then(response => response.json())
    .then(cart => {
           
      updateCartCounters();
        updateFreeShipping();

      const sectionsToUpdate = ['cart-drawer'];
      const payload = {
        sections: sectionsToUpdate.join(','),
        sections_url: window.location.pathname
      };
      
      fetch(`${window.location.pathname}?sections=${sectionsToUpdate.join(',')}`)
        .then(response => response.json())
        .then(data => {
         
          const sectionHTML = new DOMParser()
            .parseFromString(data['cart-drawer'], 'text/html')
            .querySelector('.cart-drawer').innerHTML;
            
          document.querySelector('.cart-drawer').innerHTML = sectionHTML;

          reinitRebuy();
        });
    });
  }); 

    (function() {
    const checkoutBtn = document.querySelector('.cart-drawer-checkout');
    const termsCheckbox = document.getElementById('Terms-Conditions-Drawer');
    const termsCheckboxWarning = document.getElementById('cartDrawerWarning');
    
    if (checkoutBtn && termsCheckbox) {
      checkoutBtn.addEventListener('click', function(e) {
        if (!termsCheckbox.checked) {
          e.preventDefault();
          termsCheckboxWarning.style.display = "block";
          termsCheckbox.focus();
        }
      });
    }
  })();

  document.addEventListener('DOMContentLoaded', function() {
  const termsCheckbox = document.getElementById('Terms-Conditions-Drawer');
  const termsCheckboxWarning = document.getElementById('cartDrawerWarning');
  
  if (termsCheckbox) {
    termsCheckbox.addEventListener('change', function() {
      
      if (this.checked) {
        termsCheckboxWarning.style.display = "none";
      }
    });
  }
});
